import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useBasket } from '../../../bff-api/baskets/useBasket';
import { useBasketAvailability } from '../../../bff-api/baskets/useBasketAvailability';
import { useCart } from '../../../hooks/useCart';
import { useCustomPreferenceOrDefault } from '../../../utils/api';
import { CustomPreference } from '../../../utils/api/CustomPreference';
import { getCulinaryProductItems, isBopis, isSDD } from '../../../utils/product-utils';
import { useCartState } from '../contexts/useCartState';
import { isExpiredCulinaryClass } from '../utils/cart-utils';
import { useBasketProductState } from './useBasketProductState';

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export function useCartValidation(): string[] {
    const { editorProductItems } = useCartState();
    const { getStateForProduct } = useBasketProductState();
    const { data: basket } = useBasket();
    const {
        data: availability,
        isError: isAvailabilityError,
        error: availabilityError,
        isLoading,
    } = useBasketAvailability();

    if (isAvailabilityError) {
        throw availabilityError;
    }

    const {
        hasMultipleGiftRegistries,
        countGiftRegistry,
        hasSameDayDeliveryProducts,
        hasBopisProducts,
        countCulinary,
    } = useCart();

    const classBlockedThreshold = useCustomPreferenceOrDefault(
        CustomPreference.CULINARY_CLASS_BLOCKED_THRESHOLD,
        0,
        {
            enabled: countCulinary > 0,
        },
    );

    const errors: string[] = [];

    if (isLoading) {
        return errors;
    }

    if (basket?.couponItems?.some((couponItem) => !couponItem.valid)) {
        errors.push('invalid-coupon');
    }

    if (hasMultipleGiftRegistries) {
        errors.push('multiple-gift-registries');
    }

    if (countGiftRegistry > 0 && hasSameDayDeliveryProducts) {
        errors.push('sdd-gr-not-supported');
    }

    if (countCulinary > 0) {
        const hasInvalidDates = getCulinaryProductItems(editorProductItems).some((item) =>
            isExpiredCulinaryClass(item, classBlockedThreshold),
        );

        if (hasInvalidDates) {
            errors.push('culinary-not-purchasable');
        }
    }

    const isOffline = editorProductItems.some((item) => !item.c_online);
    if (isOffline) {
        return [...errors, 'offline'];
    }

    const isNotBopisEligible = editorProductItems.some(
        (pi) => !pi.c_isBopisEligible && (isBopis(pi) || isSDD(pi)),
    );

    if (isNotBopisEligible) {
        return [...errors, 'bopis-not-eligible'];
    }

    const isOutOfStock = editorProductItems.some((pi) => {
        const productState = getStateForProduct(pi.productId);
        return productState?.availability && !productState?.availability?.availableToSell;
    });

    if (isOutOfStock) {
        return [...errors, 'out-of-stock'];
    }

    const isQuantitiesNotAvailable = editorProductItems.some((pi) => {
        const productState = getStateForProduct(pi.productId);
        return (
            productState?.availability &&
            (productState.availability.availableToSell ?? 0) < pi.quantityEditor
        );
    });

    if (isQuantitiesNotAvailable) {
        return [...errors, 'quantity-not-available'];
    }

    if (availability && !isLoading) {
        if (hasBopisProducts && (!availability.bopisStoreAvailable || !availability.bopisEnabled)) {
            return [...errors, 'bopis-not-available'];
        }

        if (
            hasSameDayDeliveryProducts &&
            (!availability.sddStoreAvailable || !availability.sddEnabled)
        ) {
            return [...errors, 'sdd-not-available'];
        }
    }

    return errors;
}
